<template>
  <div class="series-page">
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <desktop-brand-corner />

    <main class="page-shell">
      <header class="series-header">
        <h1 class="series-title">{{ seriesInfo.title }}</h1>

        <div class="uploader-row">
          <span class="uploader-avatar">
            <font-awesome-icon icon="image" />
          </span>
          <p class="uploader-text">
            <span class="uploader-name">{{ seriesInfo.uploader }}</span>
            上传
          </p>
        </div>
      </header>

      <section class="content-section">
        <h2 class="section-title">介绍</h2>
        <p class="series-desc">{{ seriesInfo.description }}</p>
      </section>

      <section class="content-section">
        <h2 class="section-title">合辑</h2>

        <div class="season-bar">
          <div ref="seasonChipsRef" class="season-chips">
            <button
              v-for="season in seasons"
              :key="season.id"
              type="button"
              class="season-chip"
              :data-season-id="season.id"
              :class="{ active: activeSeason === season.id }"
              @click="selectSeason(season.id)"
            >
              {{ season.label }}
            </button>
          </div>

          <button type="button" class="menu-toggle-btn" @click="toggleSeasonSheet">
            <font-awesome-icon icon="chevron-down" :class="{ expanded: isSeasonSheetOpen }" />
          </button>
        </div>

        <div class="episode-grid">
          <article
            v-for="episode in filteredEpisodes"
            :key="episode.id"
            class="episode-card"
            @click="openPlayPage(episode)"
          >
            <div
              class="episode-cover"
              :style="{ '--cover-start': episode.coverStart, '--cover-end': episode.coverEnd }"
            >
              <font-awesome-icon icon="image" class="cover-icon" />
              <span class="episode-tag" :class="episode.status">
                {{ episode.status === 'learned' ? `已学${episode.learnCount}遍` : '未学' }}
              </span>
            </div>

            <h3 class="episode-title">{{ episode.title }}</h3>
            <p class="episode-subtitle">{{ episode.subtitle }}</p>
          </article>
        </div>

        <div v-if="filteredEpisodes.length === 0" class="empty-state">
          当前季暂无内容
        </div>
      </section>
    </main>

    <Transition name="sheet-fade">
      <div v-if="isSeasonSheetOpen" class="sheet-mask" @click="closeSeasonSheet">
        <section class="sheet-panel" @click.stop>
          <h3 class="sheet-title">选择季数</h3>

          <button
            v-for="season in seasons"
            :key="season.id"
            type="button"
            class="sheet-option"
            :class="{ active: activeSeason === season.id }"
            @click="selectSeasonFromSheet(season.id)"
          >
            <span>{{ season.label }}</span>
            <span v-if="activeSeason === season.id" class="sheet-check">已选</span>
          </button>

          <button type="button" class="sheet-cancel" @click="closeSeasonSheet">取消</button>
        </section>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'

const seriesInfo = {
  title: 'Yakka Dee',
  uploader: '土豆麻麻育儿',
  description:
    'Yakka Dee（亚克迪）是一部CBBC的儿童电视节目，适合零基础或者刚接触英语的孩子。通过卡通人物和真人孩子的互动，鼓励孩子们开口说话。',
}

const seasons = [
  { id: 's1', label: '第一季' },
  { id: 's2', label: '第二季' },
  { id: 's3', label: '第三季' },
  { id: 's4', label: '第四季' },
  { id: 's5', label: '第五季' },
  { id: 's6', label: '第六季' },
]

const episodeWords = ['Banana', 'Apple', 'Bus', 'Dog', 'Orange', 'Water']
const coverPalette = [
  ['#e3e3e3', '#d4d4d4'],
  ['#e6e6e6', '#d8d8d8'],
  ['#e8e8e8', '#dadada'],
  ['#ebebeb', '#dddddd'],
  ['#e5e5e5', '#d5d5d5'],
  ['#e7e7e7', '#d7d7d7'],
]

const episodes = seasons.flatMap((season, seasonIndex) =>
  episodeWords.map((word, index) => {
    const [coverStart, coverEnd] = coverPalette[index % coverPalette.length]
    const learned = index === 0 || index === 3

    return {
      id: `${season.id}-${index + 1}`,
      seasonId: season.id,
      title: `yakka dee ${season.label} ${String(index + 1).padStart(2, '0')}`,
      subtitle: word,
      status: learned ? 'learned' : 'unlearned',
      learnCount: learned ? seasonIndex % 2 + 1 : 0,
      coverStart,
      coverEnd,
    }
  })
)

const activeSeason = ref(seasons[0].id)
const isSeasonSheetOpen = ref(false)
const seasonChipsRef = ref(null)
const router = useRouter()

const filteredEpisodes = computed(() =>
  episodes.filter((episode) => episode.seasonId === activeSeason.value)
)

const syncSeasonChipIntoView = () => {
  nextTick(() => {
    const chipsContainer = seasonChipsRef.value
    if (!chipsContainer) {
      return
    }

    const activeChip = chipsContainer.querySelector(
      `button[data-season-id="${activeSeason.value}"]`
    )
    if (!activeChip) {
      return
    }

    activeChip.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const selectSeason = (seasonId) => {
  activeSeason.value = seasonId
  syncSeasonChipIntoView()
}

const toggleSeasonSheet = () => {
  isSeasonSheetOpen.value = !isSeasonSheetOpen.value
}

const closeSeasonSheet = () => {
  isSeasonSheetOpen.value = false
}

const selectSeasonFromSheet = (seasonId) => {
  selectSeason(seasonId)
  closeSeasonSheet()
}

const openPlayPage = (episode) => {
  router.push({
    path: '/play',
    query: {
      season: episode.seasonId,
      episode: episode.id,
    },
  })
}
</script>

<style scoped>
.series-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  background: #f9f7f2;
  padding: 44px 0 42px;
}

.bg-decoration {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: -2;
}

.bg-decoration::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #fff8f0 0%, #f9f7f2 50%, #f4efe7 100%);
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(105px);
  opacity: 0.55;
  animation: float-orb 9s ease-in-out infinite;
}

.orb-1 {
  width: 470px;
  height: 470px;
  background: linear-gradient(135deg, #ffe3d4, #ffd2bf);
  top: -140px;
  right: -110px;
}

.orb-2 {
  width: 390px;
  height: 390px;
  background: linear-gradient(135deg, #e5f3ff, #d3e7ff);
  bottom: -90px;
  left: -95px;
  animation-delay: -2.5s;
}

.orb-3 {
  width: 280px;
  height: 280px;
  background: linear-gradient(135deg, #ffe5a8, #ffd586);
  top: 42%;
  left: 28%;
  opacity: 0.38;
  animation-delay: -4.2s;
}

@keyframes float-orb {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(28px, -30px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 18px) scale(0.95);
  }
}

.page-shell {
  position: relative;
  z-index: 1;
  width: min(1140px, calc(100% - 32px));
  margin: 0 auto;
}

.series-header {
  text-align: center;
  margin-bottom: 30px;
}

.series-title {
  font-size: clamp(36px, 4.8vw, 56px);
  line-height: 1.1;
  color: #1f2329;
  font-weight: 800;
  margin-bottom: 20px;
}

.uploader-row {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.uploader-avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #dfdfdf;
  color: #95a1aa;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.uploader-text {
  font-size: clamp(18px, 2vw, 32px);
  color: #35383f;
  font-weight: 700;
}

.uploader-name {
  color: #ff7b34;
}

.content-section {
  margin-bottom: 30px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: clamp(30px, 3.6vw, 44px);
  line-height: 1.1;
  font-weight: 800;
  color: #191b20;
}

.section-title::before {
  content: '';
  width: 10px;
  height: 36px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffc88e 0%, #ff9b4d 52%, #ff7534 100%);
  box-shadow: 0 5px 14px rgba(255, 123, 52, 0.28), inset 0 1px 1px rgba(255, 255, 255, 0.6);
}

.series-desc {
  margin-top: 16px;
  color: #535861;
  font-size: clamp(17px, 1.8vw, 28px);
  line-height: 1.7;
}

.season-bar {
  margin-top: 18px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.season-chips {
  flex: 1;
  min-width: 0;
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  padding: 10px 0;
  margin: -10px 0;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.season-chips::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.season-chip {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  border-radius: 999px;
  background: #dfdfdf;
  color: #2f2f2f;
  font-size: clamp(16px, 1.7vw, 26px);
  font-weight: 700;
  min-height: 48px;
  white-space: nowrap;
  padding: 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex: 0 0 auto;
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.season-chip:hover {
  background: #d6d6d6;
}

.season-chip:focus {
  outline: none;
}

.season-chip:focus-visible {
  box-shadow: 0 0 0 3px rgba(255, 138, 61, 0.24);
}

.season-chip.active {
  border-radius: 999px;
  background: linear-gradient(135deg, #ff9a3e 0%, #ff7a36 100%);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.38), 0 0 14px rgba(255, 138, 61, 0.3);
}

.menu-toggle-btn {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 1px solid #d7d0c6;
  background: rgba(255, 255, 255, 0.82);
  color: #4c525c;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex-shrink: 0;
}

.menu-toggle-btn:hover {
  border-color: #ffcfab;
  color: #ff7e35;
}

.menu-toggle-btn svg {
  transition: transform 0.25s ease;
}

.menu-toggle-btn .expanded {
  transform: rotate(180deg);
}

.episode-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px 16px;
}

.episode-card {
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.episode-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 0.78 / 1;
  border-radius: 14px;
  background: linear-gradient(145deg, var(--cover-start), var(--cover-end));
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-icon {
  color: #8fa0aa;
  font-size: 18px;
}

.episode-tag {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 64px;
  padding: 5px 8px;
  border-radius: 8px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.95);
}

.episode-tag.learned {
  color: #4b9e1e;
  border: 1px solid #96d562;
}

.episode-tag.unlearned {
  color: #ff8a3d;
}

.episode-title {
  margin-top: 10px;
  text-align: center;
  color: #1f2228;
  font-size: clamp(16px, 1.6vw, 24px);
  font-weight: 700;
  line-height: 1.3;
}

.episode-subtitle {
  margin-top: 4px;
  text-align: center;
  color: #2c2f36;
  font-size: clamp(17px, 1.7vw, 25px);
  line-height: 1.25;
}

.empty-state {
  padding: 28px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(223, 216, 205, 0.92);
  text-align: center;
  color: #868686;
}

.sheet-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: rgba(17, 21, 26, 0.34);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 12px 12px;
}

.sheet-panel {
  width: min(720px, 100%);
  border-radius: 24px;
  border: 1px solid rgba(221, 214, 202, 0.95);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 -12px 38px rgba(24, 25, 26, 0.16);
  padding: 10px 14px 14px;
}

.sheet-title {
  margin: 0 0 12px;
  text-align: center;
  font-size: 16px;
  color: #3b3b3b;
  font-weight: 700;
}

.sheet-option {
  width: 100%;
  border: 1px solid #e6e0d8;
  border-radius: 14px;
  background: #f8f6f2;
  color: #3f3f3f;
  font-size: 15px;
  font-weight: 600;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.25s ease;
}

.sheet-option + .sheet-option {
  margin-top: 10px;
}

.sheet-option:hover {
  border-color: #d8cec2;
  background: #f3efe9;
}

.sheet-option.active {
  border-color: #ffb788;
  background: linear-gradient(135deg, #ffeddc 0%, #ffe3d0 100%);
  color: #cf5c1f;
}

.sheet-check {
  font-size: 13px;
  font-weight: 700;
}

.sheet-cancel {
  width: 100%;
  margin-top: 12px;
  border: none;
  border-radius: 14px;
  background: #ece7de;
  color: #4c4c4c;
  font-size: 15px;
  font-weight: 700;
  padding: 13px 14px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.sheet-cancel:hover {
  background: #e2dbd0;
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.25s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-fade-enter-active .sheet-panel,
.sheet-fade-leave-active .sheet-panel {
  transition: transform 0.25s ease;
}

.sheet-fade-enter-from .sheet-panel,
.sheet-fade-leave-to .sheet-panel {
  transform: translateY(100%);
}

@media (max-width: 980px) {
  .page-shell {
    width: min(100%, calc(100% - 28px));
  }

  .episode-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .series-page {
    padding-top: 32px;
  }

  .series-title {
    margin-bottom: 14px;
  }

  .uploader-avatar {
    width: 52px;
    height: 52px;
    font-size: 15px;
  }

  .season-chip {
    min-height: 42px;
    padding: 0 20px;
  }

  .menu-toggle-btn {
    width: 42px;
    height: 42px;
    font-size: 14px;
  }

  .sheet-mask {
    padding: 0;
  }

  .sheet-panel {
    width: 100%;
    border-radius: 22px 22px 0 0;
    border-bottom: none;
    padding: 10px 12px 16px;
  }
}

@media (max-width: 480px) {
  .page-shell {
    width: calc(100% - 20px);
  }

  .section-title::before {
    width: 8px;
    height: 30px;
    box-shadow: 0 4px 10px rgba(255, 123, 52, 0.24), inset 0 1px 1px rgba(255, 255, 255, 0.55);
  }

  .season-bar {
    gap: 8px;
    margin-top: 14px;
  }

  .season-chips {
    gap: 8px;
  }

  .season-chip {
    min-height: 36px;
    padding: 0 16px;
  }

  .menu-toggle-btn {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }

  .episode-grid {
    gap: 14px 10px;
  }

  .episode-tag {
    font-size: 12px;
    min-width: 58px;
    padding: 4px 7px;
  }
}
</style>
